import axios from 'axios';
import { getApiBaseUrl } from "../utils/urlHandler";
import { debug } from "../utils/debugLogger";
import type { TikTokOptions } from "../features/tiktok/types";

const BASE_URL = getApiBaseUrl("REACT_APP_CONTENT_API_URL");

//const API_BASE_URL = "https://o6p4m9twy3.execute-api.us-east-1.amazonaws.com/Prod";
const API_URL = `${BASE_URL}/post-by-brand`;

/** Carousel child in Content DB / social payload (Instagram). */
export type PostCarouselMediaItem = {
  type: string;
  url: string;
  order: number;
};

/**
 * Normalize `carousel_media` from API (array, JSON string, or missing).
 * Sorts by `order` for publish payloads.
 */
export function normalizeCarouselMediaFromApi(
  raw: unknown
): PostCarouselMediaItem[] | undefined {
  if (raw == null) return undefined;
  let arr: unknown[] | null = null;
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return undefined;
    try {
      const parsed = JSON.parse(t) as unknown;
      if (Array.isArray(parsed)) arr = parsed;
    } catch {
      return undefined;
    }
  } else {
    return undefined;
  }
  if (arr == null || arr.length === 0) return undefined;
  const items: PostCarouselMediaItem[] = [];
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const url = typeof o.url === "string" ? o.url.trim() : "";
    if (!url) continue;
    const type = typeof o.type === "string" && o.type ? o.type : "IMAGE";
    let order = items.length;
    if (typeof o.order === "number" && Number.isFinite(o.order)) {
      order = o.order;
    } else if (typeof o.order === "string") {
      const n = parseInt(o.order, 10);
      if (Number.isFinite(n)) order = n;
    }
    items.push({ url, type, order });
  }
  if (!items.length) return undefined;
  items.sort((a, b) => a.order - b.order);
  return items;
}

// Normalized Post interface for frontend use
export interface Post {
  post_id: string;
  customer_id: string;
  platform: string;
  image_url: string;
  video_url: string;
  content: string;
  hashtags: string;
  call_to_action: string;
  scheduled_time: string;
  /** IANA zone the schedule was picked in; absent on pre-timezone rows. */
  scheduled_timezone?: string;
  status: string;
  review_link: string;
  /** Set once a review link has been generated for this post (SGL-389). */
  review_link_id?: string;
  business_profile_id: string;
  created_at: string;
  updated_at: string;
  expire_at: number | string;
  platform_post_id: string;
  platform_response: any;
  publish_type: string;
  title: string;
  gmb_account_id?: string;
  privacy_status?: string; // Optional: For YouTube privacy ('public', 'unlisted', 'private')
  made_for_kids?: boolean; // Optional: For YouTube COPPA compliance (required by YouTube API)
  tiktok_options?: TikTokOptions; // TikTok Direct Post settings + consent (SGL-170)
  post_type?: string;
  carousel_media?: PostCarouselMediaItem[];
  /** LinkedIn single document post (SGL-174): PDF rendered as a native LinkedIn document. */
  document_url?: string;
  /** LinkedIn PDF carousel (SGL-174): multiple PDFs merged server-side into one document. */
  carousel_slides?: { url: string; type?: string; caption?: string }[];
}

// Interface for the request body
export interface PostByBrandRequest {
  brand_id: string;
  text: string;
  media_type: "single_image" | "multi_image";
  color_palette_type: string;
  brand_details: {
    color_1: string;
    color_2: string;
    color_3: string;
    brand_website: string;
    brand_handle: string;
    logo_url: string;
  };
}

// Interface for Brand Post Details Response
export interface BrandPostDetailsResponse {
  data: {
    media_type: string;
    brand_id: string;
    post_id: string;
    generated_media: {
      url_hq: string;
      thumb_url: string;
      url: string;
    }[];
    status: string;
    timestamp: string;
  }[];
}

// Interface for the response structure
export interface PostDetailsResponse {
  data: {
    media_type: string;
    brand_id: string;
    post_id: string;
    generated_media: {
      url_hq: string;
      thumb_url: string;
      url: string;
    }[];
    status: string;
    timestamp: string;
  }[];
}


// Define the response interface
interface GenerateReviewResponse {
  review_url: string;
  customer_id: string;
  message: string;
  /** Present only on backends that support expiry/revoke (SGL-389). */
  link_id?: string;
  /** ISO UTC expiry; present with link_id. */
  expires_at?: string;
}

export type ReviewLinkStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'EMPTY';

export interface ReviewLinkSummary {
  link_id: string;
  url: string;
  status: ReviewLinkStatus;
  created_at: string;
  expires_at: string;
  post_count: number;
  created_by_email?: string;
  revoked_at?: string;
}


// Define the interface for the request payload
export interface GenerateImageRequest {
  prompt: string;
  aspect_ratio: "ASPECT_1_1" | "ASPECT_16_9";
  model: "V_1" | "V_2";
  magic_prompt_option: "AUTO" | "MANUAL";
  style_type: "REALISTIC" | "ABSTRACT";
  colors: { color_hex: string; color_weight: number }[];
  customer_id: string;
}

// Function to make API request
export const generateImage = async (requestBody: GenerateImageRequest): Promise<any> => {
  try {
    const response = await axios.post(API_URL, requestBody, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    debug.error("Error in generateImage API call:", error);
    throw new Error(
      error.response?.data?.message || "An error occurred while generating the image."
    );
  }
};

// Function to build query parameters dynamically
const buildQueryParams = (criteria: { [key: string]: string | string[] }) => {
  const params = new URLSearchParams();
  Object.entries(criteria).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v));
    } else if (value) {
      params.append(key, value);
    }
  });
  return params.toString();
};

// Default server-side page size for the paginated posts endpoint.
export const POSTS_PAGE_SIZE = 20;

export interface FetchPostsPage {
  posts: Post[];
  nextToken: string | null;
}

/**
 * Fetches a single page of posts. The endpoint is paginated and returns
 * { items, next_token, count }; pass the returned `nextToken` back via
 * options.nextToken to load the next page. A legacy bare-array response is
 * tolerated (treated as a single, final page).
 */
export const fetchPostsPage = async (
  customerId: string | null,
  idToken: string,
  platforms?: string[],
  status?: string,
  options?: { limit?: number; nextToken?: string | null }
): Promise<FetchPostsPage> => {
  if (!customerId) {
    return { posts: [], nextToken: null };
  }

  const queryParams = buildQueryParams({
    platform: platforms || [], // Use an empty array if not provided
    status: status || "", // Use an empty string if not provided
    limit: String(options?.limit ?? POSTS_PAGE_SIZE),
    ...(options?.nextToken ? { next_token: options.nextToken } : {}),
  });

  try {
    const response = await axios.get(`${BASE_URL}/${customerId}/posts?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    const data = response.data;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : [];
    const nextToken = Array.isArray(data) ? null : data?.next_token ?? null;

    return { posts: items.map(mapApiPostToPost), nextToken };
  } catch (error) {
    debug.error("Error fetching posts:", error);
    throw new Error("Failed to fetch posts.");
  }
};

/**
 * Fetches every post for a customer by walking the paginated endpoint until the
 * cursor is exhausted. Returns a flat array (the original fetchPosts contract),
 * so non-paginated callers are unaffected by pagination. Requests the max server
 * page size (50) and caps total pages to guard against a runaway loop.
 */
export const fetchPosts = async (
  customerId: string | null,
  idToken: string,
  platforms?: string[],
  status?: string
): Promise<Post[]> => {
  if (!customerId) {
    return [];
  }

  const MAX_PAGES = 100;
  const allPosts: Post[] = [];
  let nextToken: string | null = null;

  for (let i = 0; i < MAX_PAGES; i++) {
    const page: FetchPostsPage = await fetchPostsPage(customerId, idToken, platforms, status, {
      limit: 50,
      nextToken,
    });
    allPosts.push(...page.posts);
    nextToken = page.nextToken;
    if (!nextToken) break;
  }

  return allPosts;
};



// Service to make the API call
export const postByBrand = async (
  requestBody: PostByBrandRequest
): Promise<any> => {
  const API_URL =
    "https://o6p4m9twy3.execute-api.us-east-1.amazonaws.com/Prod/post-by-brand";

  try {
    const response = await axios.post(API_URL, requestBody);
    return response.data; // Return the response data
  } catch (error: any) {
    debug.error("Error in postByBrand service:", error);
    throw new Error(
      error.response?.data?.message || "An error occurred while making the API call."
    );
  }
};


// Function to fetch brand post details by ID
export const fetchPostByCustomerAndPostId = async (
  customerId: string,
  postId: string,
  idToken: string
): Promise<Post[]> => {
  // New endpoint: {BASE_URL}/{customer_id}/posts/{post_id}
  const endpoint = `${BASE_URL}/${customerId}/posts/${postId}`;

  try {
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    // Map each post in the array to the Post interface
    const posts = Array.isArray(response.data) ? response.data : [response.data];
    return posts.map(mapApiPostToPost);
  } catch (error: any) {
    debug.error("Error fetching brand post details:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching brand post details."
    );
  }
};


export const updatePostFields = async (
  postId: string,
  updates: Record<string, any>,
  idToken: string,
  customerId?: string,
): Promise<void> => {
  try {
    if (!customerId) throw new Error("customerId is required for updatePostFields");
    const url = `${BASE_URL}/${customerId}/posts`;
    const payload: any = {
      customer_id: customerId,
      post_id: postId,
      updates,
    };
    debug.log("Payload sent:", payload);

    await axios.put(url, payload, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${idToken}`
      },
    });

    debug.log(`Post ${postId} updated successfully. Payload:`, payload);
  } catch (error) {
    debug.error(`Error updating post ${postId}:`, error);
    throw error;
  }
};


/**
 * Generates a review HTML page by sending post IDs and customer IDs to the backend
 * @param posts Array of objects: { customer_id, post_id }
 * @param idToken Authentication token for the API request
 * @returns An object containing the review URL and additional info
 */
export const generateReviewLink = async (
  posts: { customer_id: string; post_id: string }[],
  idToken?: string
): Promise<GenerateReviewResponse> => {
  try {
    const endpoint = `${BASE_URL}/generate-html`;
    const response = await axios.post(
      endpoint,
      { posts },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
      }
    );

    return response.data;
  } catch (error) {
    debug.error('Error generating review link:', error);
    throw error;
  }
};


const reviewLinkHeaders = (idToken?: string) => ({
  'Content-Type': 'application/json',
  ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
});

/** Lists the review links created for a customer (SGL-389). */
export const listReviewLinks = async (
  customerId: string,
  idToken?: string
): Promise<ReviewLinkSummary[]> => {
  try {
    const response = await axios.get(
      `${BASE_URL}/${encodeURIComponent(customerId)}/review-links`,
      { headers: reviewLinkHeaders(idToken) }
    );
    return response.data?.links ?? [];
  } catch (error) {
    debug.error('Error listing review links:', error);
    throw error;
  }
};

/** Revokes a review link so its public page stops serving (SGL-389). */
export const revokeReviewLink = async (
  customerId: string,
  linkId: string,
  idToken?: string
): Promise<{ link_id: string; status: ReviewLinkStatus }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/${encodeURIComponent(customerId)}/review-links/${encodeURIComponent(linkId)}/revoke`,
      {},
      { headers: reviewLinkHeaders(idToken) }
    );
    return response.data;
  } catch (error) {
    debug.error('Error revoking review link:', error);
    throw error;
  }
};


/**
 * Maps API response data to the Post interface
 * @param apiPost The post data from the API
 * @returns A properly formatted Post object
 */
export const mapApiPostToPost = (apiPost: any): Post => {
  // Handle null or undefined input
  if (!apiPost) {
    return {
      post_id: '',
      customer_id: '',
      platform: '',
      image_url: '',
      video_url: '',
      content: '',
      hashtags: '',
      call_to_action: '',
      scheduled_time: '',
      status: '',
      review_link: '',
      business_profile_id: '',
      gmb_account_id: '',
      created_at: '',
      updated_at: '',
      expire_at: '',
      platform_post_id: '',
      platform_response: {},
      publish_type: '',
      title: '',
      privacy_status: undefined,
      made_for_kids: undefined,
      tiktok_options: undefined,
      post_type: undefined,
      carousel_media: undefined,
    };
  }

  const carousel_media = normalizeCarouselMediaFromApi(apiPost.carousel_media);
  const platformUpper = String(apiPost.platform ?? "").toUpperCase();
  const isInstagram = platformUpper.includes("INSTAGRAM");
  let instagram_content_format = apiPost.instagram_content_format as
    | "image"
    | "video"
    | "carousel"
    | undefined;
  if (isInstagram && !instagram_content_format) {
    if (apiPost.post_type === "CAROUSEL") instagram_content_format = "carousel";
    else if (apiPost.post_type === "SINGLE_VIDEO") instagram_content_format = "video";
    else if (carousel_media?.length) instagram_content_format = "carousel";
    else instagram_content_format = "image";
  }
  return {
    post_id: apiPost.post_id || '',
    customer_id: apiPost.customer_id || '',
    platform: apiPost.platform || '',
    image_url: apiPost.image_url || '',
    video_url: apiPost.video_url || '',
    content: apiPost.content || '',
    hashtags: apiPost.hashtags || '',
    call_to_action: apiPost.call_to_action || '',
    scheduled_time: apiPost.scheduled_time || '',
    scheduled_timezone: apiPost.scheduled_timezone || '',
    status: apiPost.status || '',
    review_link: apiPost.review_link || '',
    review_link_id: apiPost.review_link_id || undefined,
    business_profile_id: apiPost.business_profile_id || '',
    gmb_account_id: apiPost.gmb_account_id || '',
    created_at: apiPost.created_at || '',
    updated_at: apiPost.updated_at || '',
    expire_at: apiPost.expire_at || '',
    platform_post_id: apiPost.platform_post_id || '',
    platform_response: apiPost.platform_response || {},
    publish_type: apiPost.publish_type || '',
    title: apiPost.title || '',
    privacy_status: apiPost.privacy_status, // YouTube privacy status
    made_for_kids: apiPost.made_for_kids, // YouTube COPPA compliance
    tiktok_options:
      apiPost.tiktok_options && typeof apiPost.tiktok_options === "object"
        ? apiPost.tiktok_options
        : undefined,
    post_type: typeof apiPost.post_type === "string" ? apiPost.post_type : undefined,
    ...(isInstagram && instagram_content_format
      ? { instagram_content_format }
      : {}),
    ...(carousel_media?.length ? { carousel_media } : {}),
    ...(apiPost.document_url ? { document_url: apiPost.document_url } : {}),
    ...(Array.isArray(apiPost.carousel_slides) && apiPost.carousel_slides.length
      ? {
          carousel_slides: apiPost.carousel_slides.filter(
            (s: any) => s && typeof s.url === "string" && s.url
          ),
        }
      : {}),
  };
};

/**
 * Fetches posts by a list of post_ids for a given customer_id.
 * @param customerId The customer ID
 * @param postIds Array of post IDs to fetch
 * @param idToken Auth token
 * @returns Array of post objects
 */
export const fetchPostsByIds = async (
  customerId: string,
  postIds: string[],
  idToken: string
): Promise<Post[]> => {
  const endpoint = `${BASE_URL}/${customerId}/posts/batch`;
  try {
    const response = await axios.post(
      endpoint,
      { post_ids: postIds },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    // Normalize the response data to Post interface
    const posts = Array.isArray(response.data) ? response.data : [];
    const mappedPosts = posts.map(post => mapApiPostToPost(post));
    return mappedPosts;
  } catch (error) {
    debug.error('Error fetching posts by IDs:', error);
    throw error;
  }
};


// Delete post by customer_id and post_id
export const deletePost = async (
  customerId: string,
  postId: string,
  idToken: string
): Promise<void> => {
  const endpoint = `${BASE_URL}/${customerId}/posts/${postId}`;
  try {
    await axios.delete(endpoint, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    debug.log(`Post ${postId} deleted successfully.`);
  } catch (error) {
    debug.error(`Error deleting post ${postId}:`, error);
    throw error;
  }
};

/**
 * Creates multiple posts from chat-generated content
 * Content API will enrich posts with business_profile_ids and other metadata
 * @param customerId The customer ID
 * @param posts Array of AI-generated post content (minimal data)
 * @param platformMappings Business profile IDs for each platform
 * @param idToken Auth token
 * @returns Array of created post IDs
 */
export const createPostsFromChat = async (
  customerId: string,
  posts: Array<{
    platform: string;
    title: string;
    content: string;
    image_url: string;
    video_url?: string;
    hashtags: string[];
    call_to_action: string;
    scheduled_time?: string;
    scheduled_timezone?: string;
    post_type?: string;
    carousel_media?: PostCarouselMediaItem[];
  }>,
  platformMappings: Array<{
    platform: string;
    business_profile_id: string;
    gmb_account_id?: string;
  }>,
  idToken: string
): Promise<{ post_ids: string[]; message: string }> => {
  const endpoint = `${BASE_URL}/${customerId}/posts/batch-create`;
  
  try {
    debug.log('📤 Creating posts from chat:', {
      endpoint,
      postCount: posts.length,
      platforms: posts.map(p => p.platform),
      videoUrls: posts.map(p => p.video_url),
      samplePost: posts[0],
    });
    
    const response = await axios.post(
      endpoint,
      {
        posts,
        platform_mappings: platformMappings,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    
    debug.log('✅ Posts created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    debug.error('❌ Failed to create posts from chat:', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      endpoint,
    });
    throw error;
  }
};